/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

/* eslint-disable @atlaskit/design-system/no-css-tagged-template-expression */
// See https://hello.atlassian.net/wiki/spaces/EDAI/pages/4834594169/LDR+Keeping+Emotion+Global+usages
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, Global, jsx } from '@emotion/react';

import { getSelectionStyles, SelectionStyle } from '@atlaskit/editor-shared-styles';
import { fg } from '@atlaskit/platform-feature-flags';
import { N200, N700, N800 } from '@atlaskit/theme/colors';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';

import { InlineSuggestionToolbar } from '../../../pm-plugins/inline-suggestions/floating-toolbar/floating-toolbar';
import { SuggestionManager } from '../../../pm-plugins/inline-suggestions/handlers/suggestion-manager';

const highlightStyles = css`
	background-color: ${token('color.background.accent.blue.subtlest', '#E9F2FF')};
	border-bottom: 2px solid ${token('color.background.accent.blue.subtler', '#cce0ff')};
`;

const nodeHighlightStyles = css`
	border: 1px solid ${token('color.background.accent.blue.subtle', '#579DFF')};
	box-shadow: 0 0 2px 1px ${token('color.background.accent.blue.subtler', '#cce0ff')};
`;

const cellHighlightStyles = css`
	${nodeHighlightStyles}
	::after {
		border-left: 1px solid ${token('color.background.accent.blue.subtle', '#579DFF')};
		border-bottom: 1px solid ${token('color.background.accent.blue.subtle', '#579DFF')};
	}
`;

// override standard dropdown max-height 90vh to accomodate for long list of translation languages
const translationsFloatingToolbarDropdownStyles = () => {
	return css`
		[aria-label='Annotation toolbar'],
		[aria-label='Selection toolbar'] {
			[data-role='droplistContent'] {
				max-height: 400px;
				overflow-x: hidden;
				[data-testid^='ak-editor-ai-selection-toolbar-translate-button']
					[data-item-elem-before]
					> span {
					padding: 0;
				}
			}
			[data-testid='ak-editor-ai-selection-toolbar-ai-dropdown'] > span > span {
				/* override negative margin on dropdown button */
				margin: 0;
			}
		}
	`;
};

const dropdownHeadingStyles = css`
	[aria-label='Selection toolbar'] {
		[data-testid^='ak-editor-ai-selection-toolbar-dropdown-heading'] {
			cursor: default;
			> span {
				gap: initial;
			}
			[data-item-elem-before] > span {
				padding: 0;
			}

			[data-item-title] {
				text-transform: uppercase;
				font-size: 0.75rem;
				font-weight: ${token('font.weight.bold')};
				color: ${token('color.text', N200)};
			}
		}
		[data-testid^='ak-editor-ai-selection-toolbar-dropdown-heading']:hover {
			color: ${token('color.text', N700)};
		}
	}
`;

const rovoAgentsFloatingToolbarDropdownStyles = css`
	[aria-label='Annotation toolbar'],
	[aria-label='Selection toolbar'] {
		[data-role='droplistContent'] {
			[data-testid='ak-editor-ai-selection-toolbar-dropdown-view-all-agents']:not(:only-child) {
				border-block-start: 2px solid
					var(--ds-menu-seperator-color, ${token('color.border', '#e1e3e7')});
				> span {
					gap: 4px;
				}
			}

			button[data-testid^='ak-editor-ai-selection-toolbar-dropdown-agent']
				[data-item-title='true']
				> span {
				white-space: normal;
			}
		}
	}
`;

const aiSelectionToolbarButtonStyles = () => {
	if (
		// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
		editorExperiment('platform_editor_controls', 'variant1')
	) {
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus {
				padding: 0px ${token('space.075', '6px')};
			}

			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.075', '6px')};
				span:first-child {
					margin: 0;
				}
				span[role='img'],
				svg {
					padding: 0;
				}
			}
		`;
	} else {
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus,
			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.100', '8px')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] svg {
				padding-right: ${token('space.050', '4px')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] > span:only-child {
				svg {
					padding: 0px ${token('space.100', '8px')};
				}
			}
		`;
	}
};

const inlineSuggestionsStyles = () => {
	if (
		editorExperiment('editor_ai_inline_suggestion_date_v2', 'test') ||
		fg('platform_editor_ai_jira_inline_suggestion_date')
	) {
		return css`
			.ak-editor-content-area .${InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS} {
				padding: ${token('space.100', '8px')};
				border-radius: ${token('space.075', '6px')};

				/* Reset default margin on date lozenge elements to ensure padding is applied as intended */
				[class$='-DateLozenge'] {
					margin: 0;
				}
			}
			.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.underline} {
				text-decoration-line: underline;
				text-decoration-style: dashed;
				text-decoration-color: ${token('color.border.accent.blue', '#1D7AFC')};
			}

			.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.highlight} {
				background-color: ${token('color.background.selected', '#E9F2FF')};
			}

			${fg('platform_editor_ai_suggestions_completion_hints')
				? `.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.completionHint} {
				color: ${token('color.text', N800)};
				opacity: ${token('opacity.disabled', '0.5')};
				pointer-events: none;
				user-select: none;
			}`
				: ``}
		`;
	}
	return ``;
};

const inEditorStreamingStyles = () => {
	if (fg('platform_editor_ai_in_document_streaming')) {
		return css`
			.ai-inline-streaming-hidden {
				display: none;
			}
			.ai-in-editor-user-content {
				opacity: 0.2;
			}
			.ai-in-editor-user-content::selection {
				background-color: transparent;
			}
			.ai-in-editor-ai-content {
				background-color: ${token('color.background.accent.purple.subtlest')};
				border-bottom: 1px dashed ${token('color.border.accent.purple')};
			}
			.ai-in-editor-ai-content::selection {
				background-color: transparent;
			}
			.ai-in-editor-telepointer {
				position: relative;

				&::before {
					content: '';
					width: 1.5px;
					height: 25px;
					background-color: ${token('color.background.brand.bold')};
					position: absolute;
					bottom: 0;
					left: 0;
				}
				&::after {
					content: 'AI';
					position: absolute;
					display: block;
					bottom: 12px;
					left: 0;
					font-size: 10px;
					font-weight: ${token('font.weight.bold')};
					width: 12.5px;
					height: 13px;
					padding-top: 1px;
					padding-left: 1.5px;
					line-height: initial;
					border-radius: 0px 2px 2px 0px;
					color: ${token('color.text.inverse', 'white')};
					background-color: ${token('color.background.brand.bold')};
				}
			}
		`;
	}
	return ``;
};

const globalStyles = () => {
	return css`
		${translationsFloatingToolbarDropdownStyles()}
		${rovoAgentsFloatingToolbarDropdownStyles}
		${inlineSuggestionsStyles()}
		${dropdownHeadingStyles}

		.akEditor div.ProseMirror {
			.ai-modal-start-widget,
			.ai-modal-end-widget {
				position: relative;

				&::before {
					content: '';
					background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMyIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDMgMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMSAxSDBMMSAxLjg1NzE0VjE4LjE0MzNMMCAxOS4wMDA0SDNMMiAxOC4xNDMzVjEuODU3MTRMMyAxSDJIMVoiIGZpbGw9IiM1NzlERkYiLz4KPHJlY3QgeT0iMTkiIHdpZHRoPSIzIiBoZWlnaHQ9IjEiIGZpbGw9IiM1NzlERkYiLz4KPHJlY3Qgd2lkdGg9IjMiIGhlaWdodD0iMSIgZmlsbD0iIzU3OURGRiIvPgo8L3N2Zz4K');
					z-index: 1;
					display: block;
					position: absolute;
					top: 0;
					bottom: ${token('space.negative.025', '-2px')}; /* match highlight height + border */
					background-repeat: no-repeat;
					background-position-x: center;
					background-position-y: center;
					background-size: contain;
					aspect-ratio: 3/20; /* svg (width / height) */
				}
			}

			.ai-modal-start-widget::before {
				left: 0px;
				margin-left: -0.1em;
			}

			.ai-modal-end-widget::before {
				right: 0px;
				margin-right: -0.1em;
			}

			.ai-generated-content-highlight {
				${highlightStyles}
			}

			.ai-selection-highlight {
				${highlightStyles}

				/* selection from the right */
				&.ak-editor-annotation-draft,
				&.ak-editor-annotation-focus,
      &.ak-editor-annotation-blur {
					all: unset;

					${highlightStyles}
				}
			}

			.ai-selection-node .pm-table-wrapper {
				table tr td,
				table tr th {
					${getSelectionStyles([SelectionStyle.Border, SelectionStyle.Blanket])}
				}
			}

			.ai-generated-node-highlight .pm-table-wrapper {
				table tr td,
				table tr th {
					${cellHighlightStyles}
				}
			}

			// selection from the left
			.annotationView-content-wrap {
				.ak-editor-annotation-draft,
				.ak-editor-annotation-focus,
				.ak-editor-annotation-blur {
					&:has(.ai-selection-highlight) {
						all: unset;

						${highlightStyles}
					}
				}
			}

			${inEditorStreamingStyles()}
		}

		${aiSelectionToolbarButtonStyles()}
	`;
};

export const GlobalWrapper = ({ children }: { children: React.ReactNode }) => {
	return (
		<>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<Global styles={globalStyles()} />
			{children}
		</>
	);
};
